
import React, { useState } from 'react';
import { useAccessibility } from '@/components/AccessibilityProvider';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Settings, 
  Eye, 
  Type, 
  Volume2, 
  MousePointer,
  X,
  Check,
  Minus,
  Plus
} from 'lucide-react';

const AccessibilityControls = () => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    highContrast,
    fontSize,
    reducedMotion,
    screenReader,
    toggleHighContrast,
    setFontSize,
    toggleReducedMotion,
    setScreenReader
  } = useAccessibility();

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg"
        aria-label="Open accessibility controls"
      >
        <Settings className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <Card className="fixed bottom-6 right-6 z-50 p-6 bg-white shadow-2xl border-2 border-blue-200 max-w-sm">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <h3 className="font-bold text-gray-900">Accessibility</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(false)}
            aria-label="Close accessibility controls"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* High Contrast */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Eye className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium">High Contrast</span>
            </div>
            <Button
              variant={highContrast ? "default" : "outline"}
              size="sm"
              onClick={toggleHighContrast}
              aria-pressed={highContrast}
            >
              {highContrast ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500">Improves text visibility and contrast</p>
        </div>

        {/* Font Size */}
        <div className="space-y-2">
          <div className="flex items-center space-x-2 mb-2">
            <Type className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium">Font Size</span>
            <Badge variant="outline" className="text-xs">
              {fontSize.toUpperCase()}
            </Badge>
          </div>
          <div className="flex space-x-2">
            <Button
              variant={fontSize === 'small' ? "default" : "outline"}
              size="sm"
              onClick={() => setFontSize('small')}
              className="flex-1"
            >
              Small
            </Button>
            <Button
              variant={fontSize === 'medium' ? "default" : "outline"}
              size="sm"
              onClick={() => setFontSize('medium')}
              className="flex-1"
            >
              Medium
            </Button>
            <Button
              variant={fontSize === 'large' ? "default" : "outline"}
              size="sm"
              onClick={() => setFontSize('large')}
              className="flex-1"
            >
              Large
            </Button>
          </div>
        </div>

        {/* Reduced Motion */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <MousePointer className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium">Reduced Motion</span>
            </div>
            <Button
              variant={reducedMotion ? "default" : "outline"}
              size="sm"
              onClick={toggleReducedMotion}
              aria-pressed={reducedMotion}
            >
              {reducedMotion ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500">Reduces animations and transitions</p>
        </div>

        {/* Screen Reader */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Volume2 className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium">Screen Reader Mode</span>
            </div>
            <Button
              variant={screenReader ? "default" : "outline"}
              size="sm"
              onClick={() => setScreenReader(!screenReader)}
              aria-pressed={screenReader}
            >
              {screenReader ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500">Optimizes for screen reader users</p>
        </div>

        {/* Footer */}
        <div className="pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            WCAG 2.1 Level AA Compliant
          </p>
        </div>
      </div>
    </Card>
  );
};

export default AccessibilityControls;
